import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import ThemeContext from '@hig/theme-context';
import { polyfill } from 'react-lifecycles-compat';
import IconButton from '@hig/icon-button';
import Typography from '@hig/typography';
import { createCustomClassNames } from '@hig/utils';
import { CloseSUI, CloseMUI, Complete16, Complete24, Error16, Error24, Info16, Info24, Alert16, Alert24 } from '@hig/icons';

/** @type {Object.<string, string>} */
var placements = Object.freeze({
  ABOVE: "above",
  ABOVE_OVERLAY: "above-overlay",
  BETWEEN: "between",
  BELOW_OVERLAY: "below-overlay"
});

/** @type {string[]} */
var AVAILABLE_PLACEMENTS = Object.freeze(Object.values(placements));

/**
 * @todo Move into shared package to consolidate component types
 */

/** @type {Object.<string, string>} */
var types = Object.freeze({
  PRIMARY: "primary",
  COMPLETE: "complete",
  WARNING: "warning",
  URGENT: "urgent"
});

/** @type {string[]} */
var AVAILABLE_TYPES = Object.freeze(Object.values(types));

/** @type {Object.<string, string>} */
var positions = Object.freeze({
  TOP: "top",
  BOTTOM: "bottom"
});

/** @type {string[]} */
var AVAILABLE_POSITIONS = Object.freeze(Object.values(positions));

var _placements$ABOVE$pla;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var animatorPropsByPlacement = (_placements$ABOVE$pla = {}, _defineProperty(_placements$ABOVE$pla, placements.ABOVE, {
  position: positions.TOP,
  hasBounce: true,
  hasPush: true
}), _defineProperty(_placements$ABOVE$pla, placements.ABOVE_OVERLAY, {
  position: positions.TOP,
  hasBounce: true,
  hasPush: false
}), _defineProperty(_placements$ABOVE$pla, placements.BETWEEN, {
  position: positions.TOP,
  hasBounce: false,
  hasPush: true
}), _defineProperty(_placements$ABOVE$pla, placements.BELOW_OVERLAY, {
  position: positions.BOTTOM,
  hasBounce: true,
  hasPush: false
}), _placements$ABOVE$pla);

var constants = function constants(themeData) {
  var base = {
    colorMapping: {
      error: "urgent",
      primary: "info",
      complete: "success",
      urgent: "error",
      warning: "warning",
      info: "primary",
      success: "complete"
    },
    bannerActionPaddingY: themeData["density.spacings.extraExtraSmall"],
    bannerActionSpacingX: themeData["density.spacings.large"],
    bannerContentPaddingX: themeData["density.spacings.medium"],
    bannerContentSpacingMin: "125px",
    bannerDismissIconButtonWidth: "36px",
    bannerDismissIconWidth: "10px",
    bannerDismissSpacingRight: "50px",
    bannerMessageFontSize: "14px",
    bannerMessageLineHeight: "20px",
    bannerNotificationWidthMax: "900px",
    bannerNotificationWidthMin: "200px",
    bannerWrapperMinHeight: "50px"
  };

  var bannerDismissIconPadding = "calc((" + base.bannerDismissIconButtonWidth + " - " + base.bannerDismissIconWidth + ") / 2)";
  var bannerDismissSpacingRightInteractive = base.bannerActionSpacingX;
  var bannerContentSpacingY = "calc((" + base.bannerWrapperMinHeight + " - " + base.bannerMessageFontSize + ") / 2)";

  return Object.assign(base, {
    bannerContentSpacingY: bannerContentSpacingY,
    bannerDismissIconPadding: bannerDismissIconPadding,
    bannerDismissSpacingRightInteractive: bannerDismissSpacingRightInteractive,
    bannerDismissPaddingRight: "calc(" + base.bannerDismissSpacingRight + " - " + bannerDismissIconPadding + ")",
    bannerDismissPaddingRightInteractive: "calc(" + bannerDismissSpacingRightInteractive + " - " + base.bannerDismissIconPadding + ")",
    bannerDismissPaddingTopWrapping: "calc(" + bannerContentSpacingY + " - " + base.bannerDismissIconPadding + ")",
    bannerInteractionsWrapperPaddingY: "calc(" + bannerContentSpacingY + " - " + base.bannerActionPaddingY + ")",
    bannerMessagePaddingY: "calc((" + base.bannerWrapperMinHeight + " - " + base.bannerMessageLineHeight + ") / 2)"
  });
};

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function stylesheet(props, themeData) {
  var vars = constants(themeData);
  var customStylesheet = props.stylesheet;

  var styles = _extends({
    display: "flex",
    alignItems: "center",
    padding: vars.bannerActionPaddingY + " 0",
    marginLeft: vars.bannerActionSpacingX,

    "&:first-of-type": {
      marginLeft: 0
    }

  }, props.isWrappedContent && {
    "&:last-of-type": {
      borderRight: 0
    }
  });
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

/**
 * @typedef {Object} BannerActionProps
 * @property {any} children
 * @property {Function} stylesheet
 */

/**
 * @param {BannerActionProps} props
 * @returns {JSX.Element}
 */
function BannerAction(_ref) {
  var children = _ref.children,
      customStylesheet = _ref.stylesheet;

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles;
      return React.createElement(
        "div",
        {
          className: css(stylesheet({ stylesheet: customStylesheet }, resolvedRoles))
        },
        children
      );
    }
  );
}

BannerAction.propTypes = {
  children: PropTypes.node,
  stylesheet: PropTypes.func
};
BannerAction.__docgenInfo = {
  "description": "@param {BannerActionProps} props\n@returns {JSX.Element}",
  "displayName": "BannerAction",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

var statuses = Object.freeze({
  COLLAPSED: "COLLAPSED",
  COLLAPSING: "COLLAPSING",
  EXPANDED: "EXPANDED",
  EXPANDING: "EXPANDING"
});

/** Transition ease for bounce effect */
var BOUNCE_EASING = "cubic-bezier(0.175, 0.885, 0.32, 1.275)";
/** Pixels; height added to the wrapper to show the bounce animation on the inner wrapper */
var OVERLAY_HEIGHT_BUFFER = 20;
/** Milliseconds; time for the banner to expand and collapse */
var TRANSITION_DURATION = 300;
/** Milliseconds; wait time for the inner wrapper to expand after the wrapper expands */
var PUSH_DELAY = 300;
/** Pixels; default banner height used when the component isn't mounted */
var DEFAULT_HEIGHT = 50;

/**
 * @typedef {Object} StyleUpdaterParams
 * @property {HTMLDivElement} [innerWrapper]
 * @property {boolean} hasPush
 * @property {boolean} hasBounce
 * @property {string} position
 */

/**
 * @typedef {function(StyleUpdaterParams): string} StyleUpdater
 */

/**
 * @param {HTMLDivElement} [innerWrapper]
 * @returns {number}
 */
function getInnerWrapperheight(innerWrapper) {
  return innerWrapper ? innerWrapper.offsetHeight : DEFAULT_HEIGHT;
}

/** @returns {Object.<string, string>} */
function getWrapperReset() {
  return {
    transition: "",
    overflow: "",
    height: ""
  };
}

/** @returns {Object.<string, string>} */
function getInnerWrapperReset() {
  return {
    transition: "",
    transform: ""
  };
}

/** @type {StyleUpdater} */
function getWrapperTransition(_ref) {
  var hasPush = _ref.hasPush;

  return hasPush ? TRANSITION_DURATION + "ms height ease" : "";
}

/** @type {StyleUpdater} */
function getInnerWrapperExpandingTransition(_ref2) {
  var hasBounce = _ref2.hasBounce,
      hasPush = _ref2.hasPush;

  var duration = TRANSITION_DURATION + "ms";
  var property = "transform";
  var easing = hasBounce ? BOUNCE_EASING : "ease";
  var transition = [duration, property, easing];

  if (hasPush && hasBounce) {
    transition.push(PUSH_DELAY + "ms");
  }

  return transition.join(" ");
}

/** @type {StyleUpdater} */
function getInnerWrapperCollapsingTransition() {
  return TRANSITION_DURATION + "ms transform ease";
}

/** @type {StyleUpdater} */
function getWrapperExpandedHeight(_ref3) {
  var hasBounce = _ref3.hasBounce,
      innerWrapper = _ref3.innerWrapper;

  var innerWrapperHeight = getInnerWrapperheight(innerWrapper);
  var offset = hasBounce ? OVERLAY_HEIGHT_BUFFER : 0;
  var result = innerWrapperHeight + offset;

  return result + "px";
}

/** @type {StyleUpdater} */
function getWrapperCollapsedHeight(_ref4) {
  var hasPush = _ref4.hasPush,
      innerWrapper = _ref4.innerWrapper;

  if (hasPush) return "0";

  var innerWrapperHeight = getInnerWrapperheight(innerWrapper);
  var result = innerWrapperHeight + OVERLAY_HEIGHT_BUFFER;

  return result + "px";
}

/** @type {StyleUpdater} */
function getInnerWrapperCollapsedTransform(_ref5) {
  var innerWrapper = _ref5.innerWrapper,
      position = _ref5.position;

  var isBottomPlacement = position === positions.BOTTOM;
  var modifier = isBottomPlacement ? 1 : -1;
  var offset = isBottomPlacement ? OVERLAY_HEIGHT_BUFFER : 0;
  var innerWrapperHeight = getInnerWrapperheight(innerWrapper);
  var result = innerWrapperHeight * modifier + offset;

  return "translateY(" + result + "px)";
}

/** @type {StyleUpdater} */
function getInnerWrapperExpandingTransform(_ref6) {
  var hasBounce = _ref6.hasBounce,
      position = _ref6.position;

  if (hasBounce && position === positions.BOTTOM) {
    return "translateY(" + OVERLAY_HEIGHT_BUFFER + "px)";
  }

  return "";
}

/** @typedef {import("./BannerAnimator").BannerAnimatorState} BannerAnimatorState */
/** @typedef {import("./BannerAnimator").BannerAnimatorProps} BannerAnimatorProps */
// eslint-disable-next-line max-len
/** @typedef {function(BannerAnimatorState, BannerAnimatorProps): BannerAnimatorState} BannerAnimatorUpdater */

/**
 * @param {BannerAnimatorState} [prevState]
 * @param {BannerAnimatorProps} [props]
 * @returns {import("./styles").StyleUpdaterParams}
 */
function getParams() {
  var prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var innerWrapper = prevState.innerWrapper;
  var hasPush = props.hasPush,
      hasBounce = props.hasBounce,
      position = props.position;


  return {
    innerWrapper: innerWrapper,
    hasBounce: hasBounce,
    hasPush: hasPush,
    position: position
  };
}

/** @type {BannerAnimatorUpdater} */
function startExpand() {
  return {
    status: statuses.EXPANDING
  };
}

/** @type {BannerAnimatorUpdater} */
function startCollapse() {
  return {
    status: statuses.COLLAPSING
  };
}

/** @type {BannerAnimatorUpdater} */
function endExpand() {
  return {
    status: statuses.EXPANDED,
    wrapperStyle: getWrapperReset(),
    innerWrapperStyle: getInnerWrapperReset()
  };
}

/** @type {BannerAnimatorUpdater} */
function endCollapse(prevState, props) {
  var params = getParams(prevState, props);

  return {
    status: statuses.COLLAPSED,
    wrapperStyle: {
      transition: "",
      overflow: "hidden",
      height: "0"
    },
    innerWrapperStyle: {
      transition: "",
      transform: getInnerWrapperCollapsedTransform(params)
    }
  };
}

/** @type {BannerAnimatorUpdater} */
function prepareCollapse(prevState, props) {
  var params = getParams(prevState, props);

  return {
    wrapperStyle: {
      transition: "",
      overflow: "hidden",
      height: getWrapperExpandedHeight(params)
    },
    innerWrapperStyle: getInnerWrapperReset()
  };
}

/** @type {BannerAnimatorUpdater} */
function animateCollapse(prevState, props) {
  var params = getParams(prevState, props);

  return {
    wrapperStyle: {
      transition: getWrapperTransition(params),
      overflow: "hidden",
      height: getWrapperCollapsedHeight(params)
    },
    innerWrapperStyle: {
      transition: getInnerWrapperCollapsingTransition(params),
      transform: getInnerWrapperCollapsedTransform(params)
    }
  };
}

/** @type {BannerAnimatorUpdater} */
function animateExpand(prevState, props) {
  var params = getParams(prevState, props);

  return {
    wrapperStyle: {
      transition: getWrapperTransition(params),
      overflow: "hidden",
      height: getWrapperExpandedHeight(params)
    },
    innerWrapperStyle: {
      transition: getInnerWrapperExpandingTransition(params),
      transform: getInnerWrapperExpandingTransform(params)
    }
  };
}

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/**
 * @typedef {Object} BannerAnimatorProps
 * @property {boolean} [isVisible]
 * @property {boolean} [hasBounce]
 * @property {boolean} [hasPush]
 * @property {string} [position]
 * @property {function(ContainerBag): JSX.Element} children
 */

/**
 * @typedef {Object} BannerAnimatorState
 * @property {string} [status]
 * @property {Object.<string, string>} [wrapperStyle]
 * @property {Object.<string, string>} [innerWrapperStyle]
 * @property {HTMLDivElement} [wrapper]
 * @property {HTMLDivElement} [innerWrapper]
 */

/** @type {Component<BannerAnimatorProps, BannerAnimatorState>} */

var BannerAnimator = function (_Component) {
  _inherits(BannerAnimator, _Component);

  function BannerAnimator() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, BannerAnimator);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = BannerAnimator.__proto__ || Object.getPrototypeOf(BannerAnimator)).call.apply(_ref, [this].concat(args))), _this), _this.state = {}, _this.handleReady = function () {
      var status = _this.state.status;


      if (status === statuses.EXPANDING) {
        _this.expand();
      }
    }, _this.handleTransitionEnd = function (event) {
      var innerWrapper = _this.state.innerWrapper;


      if (event.target !== innerWrapper) return;

      var status = _this.state.status;


      if (status === statuses.COLLAPSING) {
        window.requestAnimationFrame(function () {
          _this.setState(endCollapse);
        });
        return;
      }

      if (status === statuses.EXPANDING) {
        window.requestAnimationFrame(function () {
          _this.setState(endExpand);
        });
      }
    }, _this.refInnerWrapper = function (innerWrapper) {
      _this.setState({ innerWrapper: innerWrapper });
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }

  /** @type {BannerAnimatorState} */


  _createClass(BannerAnimator, [{
    key: "componentDidUpdate",


    /**
     * @param {BannerAnimatorProps} prevProps
     * @param {BannerAnimatorState} prevState
     */
    value: function componentDidUpdate(prevProps, prevState) {
      var prevStatus = prevState.status;
      var status = this.state.status;


      if (prevStatus === statuses.EXPANDED && status === statuses.COLLAPSING) {
        this.collapseFromExpanded();
        return;
      }
      if (prevStatus === statuses.COLLAPSING && status === statuses.EXPANDING) {
        this.expand();
        return;
      }
      if (prevStatus === statuses.EXPANDING && status === statuses.COLLAPSING) {
        this.collapse();
      }
    }
  }, {
    key: "expand",
    value: function expand() {
      var _this2 = this;

      window.requestAnimationFrame(function () {
        _this2.setState(animateExpand);
      });
    }
  }, {
    key: "collapse",
    value: function collapse() {
      var _this3 = this;

      window.requestAnimationFrame(function () {
        _this3.setState(animateCollapse);
      });
    }
  }, {
    key: "collapseFromExpanded",
    value: function collapseFromExpanded() {
      var _this4 = this;

      window.requestAnimationFrame(function () {
        _this4.setState(prepareCollapse, function () {
          _this4.collapse();
        });
      });
    }

    /** @type {BannerAnimatorProps} */


    /** @param {TransitionEvent} event */


    /** @param {HTMLDivElement} innerWrapper */

  }, {
    key: "render",
    value: function render() {
      var renderChildren = this.props.children;
      var _state = this.state,
          status = _state.status,
          wrapperStyle = _state.wrapperStyle,
          innerWrapperStyle = _state.innerWrapperStyle;
      var handleReady = this.handleReady,
          handleTransitionEnd = this.handleTransitionEnd,
          refInnerWrapper = this.refInnerWrapper;

      var children = status === statuses.COLLAPSED ? null : renderChildren({ handleReady: handleReady });
      var isBusy = status === statuses.EXPANDING || status === statuses.COLLAPSING;

      return React.createElement(
        "div",
        {
          "aria-busy": isBusy,
          "aria-expanded": status === statuses.EXPANDED,
          style: wrapperStyle,
          onTransitionEnd: handleTransitionEnd
        },
        React.createElement(
          "div",
          { style: innerWrapperStyle, ref: refInnerWrapper },
          children
        )
      );
    }
  }], [{
    key: "getDerivedStateFromProps",


    /**
     * @param {BannerAnimatorProps} nextProps
     * @param {BannerAnimatorState} prevState
     * @returns {BannerAnimatorState | null}
     */
    value: function getDerivedStateFromProps(nextProps, prevState) {
      var isVisible = nextProps.isVisible;
      var status = prevState.status;


      if (!status) {
        return isVisible ? endExpand() : endCollapse();
      }

      switch (status) {
        case statuses.COLLAPSED:
        case statuses.COLLAPSING:
          return isVisible ? startExpand() : null;
        case statuses.EXPANDED:
        case statuses.EXPANDING:
          return isVisible ? null : startCollapse();
        default:
          // eslint-disable-next-line no-console
          console.warn("Invalid status", { status: status });
          return null;
      }
    }
  }]);

  return BannerAnimator;
}(Component);

BannerAnimator.propTypes = {
  /* eslint-disable react/no-unused-prop-types */
  /** Determines the visibility of the banner */
  isVisible: PropTypes.bool,
  /** Determines the type of animation used */
  hasBounce: PropTypes.bool,
  /** Determines the type of animation used */
  hasPush: PropTypes.bool,
  /** Determines the direction of the animation */
  position: PropTypes.oneOf(AVAILABLE_POSITIONS),
  /* eslint-enable react/no-unused-prop-types */
  /** A render prop, that renders the component to be animated */
  children: PropTypes.func.isRequired
};
BannerAnimator.defaultProps = {
  isVisible: true,
  hasBounce: true,
  hasPush: true,
  position: positions.TOP
};


var BannerAnimator$1 = polyfill(BannerAnimator);
BannerAnimator.__docgenInfo = {
  "description": "@type {Component<BannerAnimatorProps, BannerAnimatorState>}",
  "displayName": "BannerAnimator",
  "props": {
    "isVisible": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines the visibility of the banner",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    },
    "hasBounce": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines the type of animation used",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    },
    "hasPush": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines the type of animation used",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    },
    "position": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_POSITIONS"
      },
      "required": false,
      "description": "Determines the direction of the animation",
      "defaultValue": {
        "value": "positions.TOP",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "A render prop, that renders the component to be animated"
    }
  }
};

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/**
 * @typedef {Object} PresenterBag
 * @property {boolean} isWrappingContent
 * @property {function(HTMLDivElement)} refContent
 * @property {function(HTMLParagraphElement)} refNotification
 * @property {function(HTMLDivElement)} refInteractionsWrapper
 * @property {any} [children]
 */

/**
 * @typedef {Object} BannerContainerProps
 * @property {any} [actions]
 * @property {Function} [onReady]
 * @property {function(PresenterBag): any} [children]
 */

/**
 * @typedef {Object} BannerContainerState
 * @property {boolean} isWrappingActions
 * @property {boolean} isWrappingContent
 */

/** The minimum space between the notification and actions */
var MIN_CONTENT_SPACING = 125;
/** The minimum width of the notification */
var MIN_NOTIFICATION_WIDTH = 200;
/** The width of the notification at which actions will begin wrapping */
var ACTIONS_WRAPPING_THRESHOLD = 300;

/** @type {Component<BannerContainerProps, BannerContainerState>} */

var BannerContainer = function (_Component) {
  _inherits$1(BannerContainer, _Component);

  function BannerContainer() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$1(this, BannerContainer);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$1(this, (_ref = BannerContainer.__proto__ || Object.getPrototypeOf(BannerContainer)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      isWrappingActions: false,
      isWrappingContent: false
    }, _this.refContent = function (element) {
      _this.content = element;
    }, _this.refNotification = function (element) {
      _this.notification = element;
    }, _this.refInteractionsWrapper = function (element) {
      _this.interactionsWrapper = element;
    }, _this.handleResize = function () {
      _this.updateWrapping();
    }, _this.updateContentWrapping = function (callback) {
      var update = { isWrappingContent: _this.shouldWrapContent() };

      _this.setState(update, function () {
        delete _this.wrappingFrame;

        if (callback) callback();
      });
    }, _this.updateActionWrapping = function (callback) {
      var update = { isWrappingActions: _this.shouldWrapActions() };

      _this.setState(update, function () {
        _this.wrappingFrame = window.requestAnimationFrame(function () {
          _this.updateContentWrapping(callback);
        });
      });
    }, _temp), _possibleConstructorReturn$1(_this, _ret);
  }

  /** @type {BannerContainerState} */


  _createClass$1(BannerContainer, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.bindResize();
      this.updateWrapping(this.props.onReady);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.unbindResize();
      this.cancelWrappingUpdate();
    }

    /** @type {BannerContainerProps | any} */

    /** @type {HTMLDivElement | undefined} */

    /** @type {HTMLDivElement | undefined} */

    /** @type {HTMLParagraphElement | undefined} */

    /** @type {number | undefined} */


    /**
     * @param {HTMLDivElement} element
     */


    /**
     * @param {HTMLParagraphElement} element
     */


    /**
     * @param {HTMLDivElement} element
     */

  }, {
    key: "shouldWrapActions",


    /**
     * Determines whether actions should wrap on the next repaint
     * @returns {boolean}
     */
    value: function shouldWrapActions() {
      var interactionsWrapper = this.interactionsWrapper;


      if (!interactionsWrapper) return false;

      var isWrappingActions = this.state.isWrappingActions;

      var notificationWidth = isWrappingActions ? ACTIONS_WRAPPING_THRESHOLD + interactionsWrapper.offsetWidth * 2 : ACTIONS_WRAPPING_THRESHOLD;

      return this.willContentOverflow(notificationWidth);
    }

    /**
     * Determines whether content should wrap on the next repaint
     * @returns {boolean}
     */

  }, {
    key: "shouldWrapContent",
    value: function shouldWrapContent() {
      return this.willContentOverflow(MIN_NOTIFICATION_WIDTH);
    }

    /**
     * Determines whether the content will overflow its container
     * with the given width of the notification element.
     *
     * @param {number} notificationWidth
     * @returns {boolean}
     */

  }, {
    key: "willContentOverflow",
    value: function willContentOverflow(notificationWidth) {
      var content = this.content,
          interactionsWrapper = this.interactionsWrapper;


      if (!content || !interactionsWrapper) return false;

      var contentWidth = content.offsetWidth;
      var actionsWidth = interactionsWrapper.offsetWidth;
      var contentChildrenWidth = notificationWidth + MIN_CONTENT_SPACING + actionsWidth;

      return contentChildrenWidth > contentWidth;
    }

    /**
     * @param {Function} callback
     */


    /**
     * @param {Function} callback
     */

  }, {
    key: "updateWrapping",


    /**
     * Asynchronously updates the wrapping behavior of the presenter
     * @param {Function} callback
     */
    value: function updateWrapping(callback) {
      var _this2 = this;

      if (this.wrappingFrame !== undefined) return;

      this.wrappingFrame = window.requestAnimationFrame(function () {
        _this2.updateActionWrapping(callback);
      });
    }
  }, {
    key: "cancelWrappingUpdate",
    value: function cancelWrappingUpdate() {
      if (this.wrappingFrame !== undefined) {
        window.cancelAnimationFrame(this.wrappingFrame);
      }
    }
  }, {
    key: "bindResize",
    value: function bindResize() {
      window.addEventListener("resize", this.handleResize);
    }
  }, {
    key: "unbindResize",
    value: function unbindResize() {
      window.removeEventListener("resize", this.handleResize);
    }

    /**
     * @returns {PresenterBag}
     */

  }, {
    key: "createPresenterBag",
    value: function createPresenterBag() {
      var isWrappingContent = this.state.isWrappingContent;
      var refContent = this.refContent,
          refNotification = this.refNotification,
          refInteractionsWrapper = this.refInteractionsWrapper;


      return {
        isWrappingContent: isWrappingContent,
        refContent: refContent,
        refNotification: refNotification,
        refInteractionsWrapper: refInteractionsWrapper,
        actions: this.renderActions()
      };
    }

    /** @returns {any} */

  }, {
    key: "renderActions",
    value: function renderActions() {
      var actions = this.props.actions;


      if (typeof actions !== "function") return actions;

      var _state = this.state,
          isWrappingContent = _state.isWrappingContent,
          isWrappingActions = _state.isWrappingActions;

      var actionsBag = { isWrappingContent: isWrappingContent, isWrappingActions: isWrappingActions };

      return actions(actionsBag);
    }
  }, {
    key: "render",
    value: function render() {
      var renderPresenter = this.props.children;

      var presenterBag = this.createPresenterBag();

      return renderPresenter(presenterBag);
    }
  }]);

  return BannerContainer;
}(Component);

BannerContainer.propTypes = {
  /** Banner actions; Any JSX, or a render prop function */
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Called after the component has been mounted, and dynamically resized */
  onReady: PropTypes.func,
  /** A render prop function to render a `BannerPresenter` */
  children: PropTypes.func.isRequired
};
BannerContainer.__docgenInfo = {
  "description": "@type {Component<BannerContainerProps, BannerContainerState>}",
  "displayName": "BannerContainer",
  "props": {
    "actions": {
      "type": {
        "name": "union",
        "value": [{
          "name": "node"
        }, {
          "name": "func"
        }]
      },
      "required": false,
      "description": "Banner actions; Any JSX, or a render prop function"
    },
    "onReady": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called after the component has been mounted, and dynamically resized"
    },
    "children": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": "A render prop function to render a `BannerPresenter`"
    }
  }
};

function stylesheet$1(props, themeData) {
  var customStylesheet = props.stylesheet;

  var styles = {
    display: "flex"
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

/**
 * @typedef {Object} BannerInteractionsProps
 * @property {boolean} [isWrappingActions]
 * @property {any} [children]
 * @property {Function} [stylesheet]
 */

/**
 * @param {BannerInteractionsProps} props
 * @returns {JSX.Element}
 */
function BannerInteractions(props) {
  var children = props.children,
      customStylesheet = props.stylesheet;


  return React.createElement(
    "div",
    { className: css(stylesheet$1({ stylesheet: customStylesheet }, {})) },
    children
  );
}

BannerInteractions.defaultProps = {
  isWrappingActions: false
};

BannerInteractions.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  isWrappingActions: PropTypes.bool,
  children: PropTypes.node,
  stylesheet: PropTypes.func
};
BannerInteractions.__docgenInfo = {
  "description": "@param {BannerInteractionsProps} props\n@returns {JSX.Element}",
  "displayName": "BannerInteractions",
  "props": {
    "isWrappingActions": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function stylesheet$2(props, themeData) {
  var validTypes = Object.values(types);
  var vars = constants(themeData);
  var customStylesheet = props.stylesheet;

  var styles = {
    // .hig__banner
    banner: _extends$1({
      display: "flex",
      boxSizing: "border-box",
      minHeight: vars.bannerWrapperMinHeight,
      borderWidth: "1px 0",
      borderStyle: "solid"

    }, validTypes.includes(props.type) && {
      borderColor: themeData["banner." + vars.colorMapping[props.type] + ".borderColor"],
      backgroundColor: themeData["banner." + vars.colorMapping[props.type] + ".backgroundColor"]
    }),

    bannerBackground: {
      backgroundColor: themeData["colorScheme.surface.level100"]
    },

    // .hig__banner__content
    content: _extends$1({
      display: "flex",
      flexGrow: "1",
      flexShrink: "1",
      alignItems: "center",
      justifyContent: "space-between",
      margin: vars.bannerMessagePaddingY + " 0 0 " + vars.bannerContentPaddingX,
      overflow: "hidden"

    }, !props.isWrappingContent && {
      flexDirection: "column",
      alignItems: "flex-start"
    }),

    // .hig__banner__message
    message: _extends$1({
      minWidth: vars.bannerNotificationWidthMin,
      maxWidth: vars.bannerNotificationWidthMax,
      paddingBottom: vars.bannerMessagePaddingY,
      margin: "0 auto 0 0",
      textAlign: "left"

    }, props.hasActions && {
      marginRight: vars.bannerContentSpacingMin
    }, props.isWrappingContent && {
      marginRight: 0
    }),

    // .hig__banner__dismiss-button
    dismissButton: _extends$1({
      display: "flex",
      alignItems: "center",
      flexGrow: "0",
      flexShrink: "0",
      paddingRight: vars.bannerDismissPaddingRight

    }, props.hasActions && {
      paddingRight: vars.bannerDismissPaddingRightInteractive
    }, props.isWrappingContent && {
      alignItems: "flex-start",
      paddingTop: vars.bannerDismissPaddingTopWrapping
    }, {

      "& svg": {
        cursor: "pointer"
      },

      "& svg *": {
        fill: themeData["colorScheme.icon.default"]
      },

      "&:hover svg *": {
        fill: themeData["colorScheme.reference.accent"]
      }
    }),

    // .hig__banner__interactions-wrapper
    interactionsWrapper: {
      flexShrink: "0",
      paddingRight: vars.bannerActionSpacingX,
      paddingBottom: vars.bannerInteractionsWrapperPaddingY
    },

    // .hig__banner__icon-background
    iconBackground: _extends$1({
      display: "flex",
      flexGrow: 0,
      flexShrink: 0,
      alignItems: "center",
      justifyContent: "center",
      width: vars.bannerWrapperMinHeight,
      minHeight: vars.bannerWrapperMinHeight,
      margin: 0

    }, validTypes.includes(props.type) && {
      backgroundColor: themeData["banner." + vars.colorMapping[props.type] + ".iconField.backgroundColor"]
    }, {
      "svg *": {
        fill: "white"
      }
    })
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/**
 * @typedef {Object} StyledProps
 * @property {Function} [innerRef]
 * @property {any} [children]
 */

/**
 * @typedef {Object} WrapperProps
 * @property {string} type
 * @property {boolean} hasActions
 * @property {boolean} isWrappingContent
 * @property {any} children
 */

/**
 * @param {WrapperProps} props
 * @returns {JSX.Element}
 */
function Wrapper(props) {
  var type = props.type,
      children = props.children,
      otherProps = _objectWithoutProperties(props, ["type", "children"]);

  var className = otherProps.className;

  var bannerClassName = createCustomClassNames(className, "banner");
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref) {
      var resolvedRoles = _ref.resolvedRoles;
      return React.createElement(
        "div",
        {
          className: cx(css(stylesheet$2(props, resolvedRoles).bannerBackground), className)
        },
        React.createElement(
          "div",
          {
            role: "alert",
            "aria-live": type === types.URGENT ? "assertive" : "polite",
            className: cx(css(stylesheet$2(props, resolvedRoles).banner), bannerClassName)
          },
          children
        )
      );
    }
  );
}

/**
 * @param {StyledProps} props
 * @returns {JSX.Element}
 */
function Content(_ref2) {
  var isWrappingContent = _ref2.isWrappingContent,
      innerRef = _ref2.innerRef,
      children = _ref2.children,
      customStylesheet = _ref2.stylesheet,
      otherProps = _objectWithoutProperties(_ref2, ["isWrappingContent", "innerRef", "children", "stylesheet"]);

  var className = otherProps.className;

  var contentClassName = createCustomClassNames(className, "content");
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref3) {
      var resolvedRoles = _ref3.resolvedRoles;
      return React.createElement(
        "div",
        {
          className: cx(css(stylesheet$2({ isWrappingContent: isWrappingContent, stylesheet: customStylesheet }, resolvedRoles).content), contentClassName),
          ref: innerRef
        },
        children
      );
    }
  );
}

/**
 * @typedef {Object} DismissButtonProps
 * @property {Function} [onClick]
 */

/**
 * @param {DismissButtonProps} props
 * @returns {JSX.Element}
 */
function DismissButton(_ref4) {
  var onClick = _ref4.onClick,
      customStylesheet = _ref4.stylesheet,
      otherProps = _objectWithoutProperties(_ref4, ["onClick", "stylesheet"]);

  var className = otherProps.className;

  var dismissClassName = createCustomClassNames(className, "dismiss-button");
  var dismissIconClassName = createCustomClassNames(className, "dismiss-button-icon");
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref5) {
      var resolvedRoles = _ref5.resolvedRoles,
          metadata = _ref5.metadata;

      var closeIcon = metadata.densityId === "medium-density" ? React.createElement(CloseMUI, null) : React.createElement(CloseSUI, null);
      return React.createElement(
        "div",
        {
          className: cx(css(stylesheet$2({ stylesheet: customStylesheet }, resolvedRoles).dismissButton), dismissClassName)
        },
        React.createElement(IconButton, _extends$2({}, otherProps, {
          className: dismissIconClassName,
          icon: closeIcon,
          onClick: onClick
        }))
      );
    }
  );
}

/**
 * @param {StyledProps} props
 * @returns {JSX.Element}
 */
function Notification(_ref6) {
  var innerRef = _ref6.innerRef,
      children = _ref6.children,
      customStylesheet = _ref6.stylesheet,
      otherProps = _objectWithoutProperties(_ref6, ["innerRef", "children", "stylesheet"]);

  var className = otherProps.className;

  var notifClassName = createCustomClassNames(className, "message");
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref7) {
      var resolvedRoles = _ref7.resolvedRoles;
      return React.createElement(
        "div",
        {
          className: cx(css(stylesheet$2({ stylesheet: customStylesheet }, resolvedRoles).message), notifClassName),
          ref: innerRef
        },
        children
      );
    }
  );
}

/**
 * @param {StyledProps} props
 * @returns {JSX.Element}
 */
function Message(_ref8) {
  var children = _ref8.children,
      otherProps = _objectWithoutProperties(_ref8, ["children"]);

  var className = otherProps.className;

  var notifClassName = createCustomClassNames(className, "message-text");
  if (typeof children === "string") {
    return React.createElement(
      Typography,
      { className: notifClassName, variant: "body" },
      children
    );
  }

  return children;
}

/**
 * @param {StyledProps} props
 * @returns {JSX.Element}
 */
function InteractionsWrapper(_ref9) {
  var innerRef = _ref9.innerRef,
      children = _ref9.children,
      customStylesheet = _ref9.stylesheet,
      otherProps = _objectWithoutProperties(_ref9, ["innerRef", "children", "stylesheet"]);

  var className = otherProps.className;

  var interactionClassName = createCustomClassNames(className, "interactions-wrapper");
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref10) {
      var resolvedRoles = _ref10.resolvedRoles;
      return React.createElement(
        "div",
        {
          className: cx(css(stylesheet$2({ stylesheet: customStylesheet }, resolvedRoles).interactionsWrapper), interactionClassName),
          ref: innerRef
        },
        children
      );
    }
  );
}

/**
 * @property {string} type
 */

function IconBackground(_ref11) {
  var _iconNamesByType;

  var type = _ref11.type,
      customStylesheet = _ref11.stylesheet,
      otherProps = _objectWithoutProperties(_ref11, ["type", "stylesheet"]);

  var className = otherProps.className;

  var iconClassName = createCustomClassNames(className, "icon-background");
  var iconImageClassName = createCustomClassNames(className, "icon-background-image");

  var iconNamesByType = (_iconNamesByType = {}, _defineProperty$1(_iconNamesByType, types.PRIMARY, {
    high: Info16,
    medium: Info24
  }), _defineProperty$1(_iconNamesByType, types.COMPLETE, {
    high: Complete16,
    medium: Complete24
  }), _defineProperty$1(_iconNamesByType, types.WARNING, {
    high: Alert16,
    medium: Alert24
  }), _defineProperty$1(_iconNamesByType, types.URGENT, {
    high: Error16,
    medium: Error24
  }), _iconNamesByType);
  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref12) {
      var resolvedRoles = _ref12.resolvedRoles,
          metadata = _ref12.metadata;

      var density = metadata.densityId === "medium-density" ? "medium" : "high";
      var Icon = iconNamesByType[type][density];
      return React.createElement(
        "figure",
        {
          className: cx(css(stylesheet$2({ type: type, stylesheet: customStylesheet }, resolvedRoles).iconBackground), iconClassName)
        },
        React.createElement(Icon, { className: iconImageClassName })
      );
    }
  );
}

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/**
 * @typedef {Object} BannerPresenterProps
 * @property {string} [type]
 * @property {any} [actions]
 * @property {string} [dismissButtonTitle]
 * @property {Function} [onDismiss]
 * @property {boolean} [isWrappingContent]
 * @property {function(HTMLDivElement): any} [refContent]
 * @property {function(HTMLParagraphElement): any} [refNotification]
 * @property {function(HTMLDivElement): any} [refInteractionsWrapper]
 * @property {any} [children]
 * @property {Function} [stylesheet]
 */

/**
 * @param {BannerPresenterProps} props
 * @returns {JSX.Element}
 * @type {Component<BannerPresenterProps>}
 */
function BannerPresenter(props) {
  var type = props.type,
      actions = props.actions,
      dismissButtonTitle = props.dismissButtonTitle,
      onDismiss = props.onDismiss,
      isWrappingContent = props.isWrappingContent,
      refContent = props.refContent,
      refNotification = props.refNotification,
      refInteractionsWrapper = props.refInteractionsWrapper,
      message = props.children,
      customStylesheet = props.stylesheet,
      otherProps = _objectWithoutProperties$1(props, ["type", "actions", "dismissButtonTitle", "onDismiss", "isWrappingContent", "refContent", "refNotification", "refInteractionsWrapper", "children", "stylesheet"]);

  var hasActions = React.Children.count(actions) > 0;
  var className = otherProps.className;

  return React.createElement(
    Wrapper,
    {
      className: className,
      type: type,
      hasActions: hasActions,
      isWrappingContent: isWrappingContent,
      stylesheet: customStylesheet
    },
    React.createElement(IconBackground, {
      className: className,
      type: type,
      stylesheet: customStylesheet
    }),
    React.createElement(
      Content,
      {
        className: className,
        isWrappingContent: true,
        innerRef: refContent,
        stylesheet: customStylesheet
      },
      React.createElement(
        Notification,
        {
          className: className,
          innerRef: refNotification,
          stylesheet: customStylesheet
        },
        React.createElement(
          Message,
          { className: className },
          message
        )
      ),
      hasActions ? React.createElement(
        InteractionsWrapper,
        {
          className: className,
          innerRef: refInteractionsWrapper,
          stylesheet: customStylesheet
        },
        actions
      ) : null
    ),
    onDismiss ? React.createElement(DismissButton, {
      className: className,
      title: dismissButtonTitle,
      onClick: onDismiss,
      stylesheet: customStylesheet
    }) : null
  );
}

/** @type {BannerPresenterProps} */
BannerPresenter.defaultProps = {
  type: types.PRIMARY,
  dismissButtonTitle: "Close",
  isWrappingContent: false,
  children: "Message"
};

BannerPresenter.propTypes = {
  /** Indicates the style of banner */
  type: PropTypes.oneOf(AVAILABLE_TYPES),
  /** Banner actions */
  actions: PropTypes.node,
  /** Accessibility text for the dismiss button */
  dismissButtonTitle: PropTypes.string,
  /** Called when the banner is dismissed */
  onDismiss: PropTypes.func,
  /** Determines whether the banner content wraps */
  isWrappingContent: PropTypes.bool,
  /** References content element */
  refContent: PropTypes.func,
  /** References notification element */
  refNotification: PropTypes.func,
  /** References interactions wrapper element */
  refInteractionsWrapper: PropTypes.func,
  /** The displayed message */
  children: PropTypes.node,
  /** Adds custom/overriding style */
  stylesheet: PropTypes.func
};
BannerPresenter.__docgenInfo = {
  "description": "@param {BannerPresenterProps} props\n@returns {JSX.Element}\n@type {Component<BannerPresenterProps>}",
  "displayName": "BannerPresenter",
  "props": {
    "type": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_TYPES"
      },
      "required": false,
      "description": "Indicates the style of banner",
      "defaultValue": {
        "value": "types.PRIMARY",
        "computed": true
      }
    },
    "actions": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Banner actions"
    },
    "dismissButtonTitle": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Accessibility text for the dismiss button",
      "defaultValue": {
        "value": "\"Close\"",
        "computed": false
      }
    },
    "onDismiss": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when the banner is dismissed"
    },
    "isWrappingContent": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Determines whether the banner content wraps",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "refContent": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "References content element"
    },
    "refNotification": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "References notification element"
    },
    "refInteractionsWrapper": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "References interactions wrapper element"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "The displayed message",
      "defaultValue": {
        "value": "\"Message\"",
        "computed": false
      }
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding style"
    }
  }
};

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$2 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$2(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/**
 * @typedef {Object} BannerProps
 * @property {string} [type]
 * @property {string} [placement]
 * @property {string} [label]
 * @property {string} [labelledBy]
 * @property {any} [actions]
 * @property {string} [dismissButtonTitle]
 * @property {Function} [onDismiss]
 * @property {boolean} [isVisible]
 * @property {any} [children]
 * @property {Function} [stylesheet]
 */

/** @type {Component<BannerProps>} */

var Banner = function (_Component) {
  _inherits$2(Banner, _Component);

  function Banner() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$2(this, Banner);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$2(this, (_ref = Banner.__proto__ || Object.getPrototypeOf(Banner)).call.apply(_ref, [this].concat(args))), _this), _this.renderPresenter = function (presenterBag) {
      var presenterProps = _extends$3({}, _this.props, presenterBag);

      var children = presenterProps.children,
          customStylesheet = presenterProps.stylesheet,
          otherProps = _objectWithoutProperties$2(presenterProps, ["children", "stylesheet"]);

      return React.createElement(
        BannerPresenter,
        _extends$3({}, otherProps, { stylesheet: customStylesheet }),
        children
      );
    }, _this.renderContainer = function (_ref2) {
      var handleReady = _ref2.handleReady;
      var actions = _this.props.actions;
      var _this2 = _this,
          renderPresenter = _this2.renderPresenter;


      return React.createElement(
        BannerContainer,
        { actions: actions, onReady: handleReady },
        renderPresenter
      );
    }, _temp), _possibleConstructorReturn$2(_this, _ret);
  }

  /**
   * @param {import("./BannerContainer").PresenterBag} presenterBag
   */


  /** @type {BannerProps | any} */


  /**
   * @param {import("./BannerAnimator").ContainerBag} containerBag
   */


  _createClass$2(Banner, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          isVisible = _props.isVisible,
          placement = _props.placement;
      var renderContainer = this.renderContainer;


      return React.createElement(
        BannerAnimator$1,
        _extends$3({
          isVisible: isVisible
        }, animatorPropsByPlacement[placement]),
        renderContainer
      );
    }
  }]);

  return Banner;
}(Component);

Banner.AVAILABLE_PLACEMENTS = AVAILABLE_PLACEMENTS;
Banner.AVAILABLE_TYPES = AVAILABLE_TYPES;
Banner.placements = placements;
Banner.types = types;
Banner.Action = BannerAction;
Banner.Interactions = BannerInteractions;
Banner.Presenter = BannerPresenter;
Banner.propTypes = {
  /** Indicates the style of banner */
  type: PropTypes.oneOf(AVAILABLE_TYPES),
  /** Determines the intended placement of banner */
  placement: PropTypes.oneOf(AVAILABLE_PLACEMENTS),
  /** The label of the message displayed */
  label: PropTypes.string,
  /** The ID used for ARIA labeling */
  labelledBy: PropTypes.string,
  /** Banner actions; Any JSX, or a render prop function */
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Accessibility text for the dismiss button */
  dismissButtonTitle: PropTypes.string,
  /** Called when the banner is dismissed
   *  If this is not supplied the close button will not appear
   */
  onDismiss: PropTypes.func,
  /** Animation; Determines the visibility of the banner */
  isVisible: PropTypes.bool,
  /** The displayed message */
  children: PropTypes.node,
  /* Adds custom/overriding style */
  stylesheet: PropTypes.func
};
Banner.__docgenInfo = {
  "description": "@type {Component<BannerProps>}",
  "displayName": "Banner",
  "props": {
    "type": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_TYPES"
      },
      "required": false,
      "description": "Indicates the style of banner"
    },
    "placement": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_PLACEMENTS"
      },
      "required": false,
      "description": "Determines the intended placement of banner"
    },
    "label": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The label of the message displayed"
    },
    "labelledBy": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The ID used for ARIA labeling"
    },
    "actions": {
      "type": {
        "name": "union",
        "value": [{
          "name": "node"
        }, {
          "name": "func"
        }]
      },
      "required": false,
      "description": "Banner actions; Any JSX, or a render prop function"
    },
    "dismissButtonTitle": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Accessibility text for the dismiss button"
    },
    "onDismiss": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when the banner is dismissed\n If this is not supplied the close button will not appear"
    },
    "isVisible": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Animation; Determines the visibility of the banner"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "The displayed message"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

export default Banner;
export { BannerAction, BannerAnimator$1 as BannerAnimator, BannerInteractions, BannerPresenter, placements, AVAILABLE_PLACEMENTS, types, AVAILABLE_TYPES };
